import { DarkTheme, DefaultTheme, ThemeProvider } from '@react-navigation/native';
import { Stack, usePathname } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useEffect } from 'react';
import 'react-native-reanimated';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { AppProvider } from '@/context/AppContext';
import { useAuth } from '@/hooks/use-auth';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { init, setUserId, setUserProperties, trackEvent } from '@/lib/analytics';
import { initSentry, Sentry } from '@/lib/sentry';

export const unstable_settings = {
  initialRouteName: 'index',
};

/**
 * Syncs Amplitude identity (userId + user properties) as soon as auth state is known.
 * Runs at root so identity is set before any screen fires events, reducing anonymous users.
 */
function AnalyticsIdentitySync() {
  const { loading, user } = useAuth();
  useEffect(() => {
    if (loading) return;
    setUserId(user?.id ?? null);
    if (user) {
      const name =
        (user.user_metadata?.full_name as string | undefined)?.trim() ||
        (user.user_metadata?.display_name as string | undefined)?.trim() ||
        undefined;
      setUserProperties({ email: user.email ?? undefined, name: name || undefined });
    }
  }, [loading, user?.id, user?.email, user?.user_metadata]);
  return null;
}

function ScreenViewTracker() {
  const pathname = usePathname();
  useEffect(() => {
    if (pathname) {
      trackEvent('Screen View', { screen_name: pathname });
    }
  }, [pathname]);
  return null;
}

function RootLayout() {
  const colorScheme = useColorScheme();

  useEffect(() => {
    initSentry();
    init();
  }, []);

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <AppProvider>
          <ThemeProvider value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}>
            <AnalyticsIdentitySync />
            <ScreenViewTracker />
            <Stack>
              <Stack.Screen name="index" options={{ headerShown: false }} />
              <Stack.Screen name="(auth)" options={{ headerShown: false }} />
              <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
              <Stack.Screen name="create-household" options={{ headerShown: false, title: 'Create household' }} />
              <Stack.Screen name="change-email" options={{ headerShown: false, title: 'Edit profile' }} />
              <Stack.Screen name="change-password" options={{ headerShown: false, title: 'Change password' }} />
              <Stack.Screen name="delete-account" options={{ headerShown: false, title: 'Delete account' }} />
              <Stack.Screen name="join-invite" options={{ headerShown: false, title: 'Join household' }} />
              <Stack.Screen name="invite" options={{ headerShown: false, title: 'Invite member' }} />
              <Stack.Screen name="(public)" options={{ headerShown: false }} />
              <Stack.Screen name="modal" options={{ presentation: 'modal', title: 'Modal' }} />
            </Stack>
            <StatusBar style="auto" />
          </ThemeProvider>
        </AppProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}

export default Sentry.wrap(RootLayout);
